import React from 'react';
import { PdfImageModeInfo, ToolCategory } from '../types';

interface PdfImageModeSelectorProps {
    pdfImageModes: PdfImageModeInfo[];
    navigateTo: (path: string) => void;
}

const ToolCard: React.FC<{ tool: PdfImageModeInfo; navigateTo: (path: string) => void; }> = ({ tool, navigateTo }) => {
    const Icon = tool.icon;
    
    const handleNavigate = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
        e.preventDefault();
        navigateTo(path);
    };

    return (
        <a 
            href={`/tool/${tool.id}`}
            onClick={(e) => handleNavigate(e, `/tool/${tool.id}`)}
            className="group relative flex flex-col justify-between h-full bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-lg shadow-slate-200/80 dark:shadow-none border border-slate-200/80 dark:border-slate-800 hover:shadow-xl dark:hover:bg-slate-800/50 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
        >
            <div className="flex items-start gap-4">
                <div className="p-2 bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-100 dark:border-slate-700 transition-colors flex-shrink-0">
                    <Icon className="w-8 h-8" />
                </div>
                <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white transition-colors line-clamp-1">{tool.label}</h3>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 line-clamp-2 min-h-[2.5rem] transition-colors">{tool.description || 'Fast, free, and secure client-side PDF tool.'}</p>
                </div>
            </div>
        </a>
    );
};

export const PdfImageModeSelector: React.FC<PdfImageModeSelectorProps> = ({ pdfImageModes, navigateTo }) => {
    const categoryOrder: ToolCategory[] = ['Organize PDF', 'Optimize PDF', 'Convert to PDF', 'Convert from PDF', 'Edit PDF'];

    const toolsByCategory = categoryOrder.map(category => ({
        category,
        tools: pdfImageModes.filter(tool => tool.category === category)
    })).filter(group => group.tools.length > 0);

    return (
        <div className="space-y-12">
            {toolsByCategory.map(({ category, tools }) => (
                <div key={category}>
                    <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-6 border-b border-slate-200 dark:border-slate-800 pb-2">{category}</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {tools.map((tool) => (
                            <ToolCard key={tool.id} tool={tool} navigateTo={navigateTo} />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};
